import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  calculateRevenue,
  calculateRevenueBulk,
  searchRevenueProducts,
} from '../../api/revenueCalculator';
import {
  PageHeader,
  PanelHeader,
  StatCard,
  StatusBanner,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { CopyableValue } from '../../components/ui/CopyableValue';
import {
  downloadBulkResultsWorkbook,
  downloadBulkTemplate,
  parseBulkAsinWorkbook,
} from './bulkExcel';

const ASIN_PATTERN = /^[A-Z0-9]{10}$/i;

const MARKETPLACES = [
  { value: 'IN', label: 'India', currency: 'INR' },
  { value: 'US', label: 'United States', currency: 'USD' },
];

function formatMoney(value, currency = 'INR') {
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatDimensions(dimensions) {
  if (!dimensions) return '—';
  const { length, width, height } = dimensions;
  if (!length && !width && !height) return '—';
  return `${length ?? '—'} × ${width ?? '—'} × ${height ?? '—'} cm`;
}

function isAsin(value) {
  return ASIN_PATTERN.test(value.trim());
}

function applySpApiPricing(product, setItemPrice, setShippingCharge) {
  if (product?.featuredOfferPrice != null) {
    setItemPrice(String(product.featuredOfferPrice));
  }
  if (product?.featuredOfferShipping != null) {
    setShippingCharge(String(product.featuredOfferShipping));
  }
}

function CurrencyInput({ label, value, onChange, currency, hint }) {
  const symbol = currency === 'INR' ? '₹' : '$';

  return (
    <label className="grid gap-1.5">
      <span className={labelClassName}>{label}</span>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[rgb(var(--crm-text-muted))]">
          {symbol}
        </span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClassName} pl-8`}
        />
      </div>
      {hint ? <span className="crm-text-muted text-xs">{hint}</span> : null}
    </label>
  );
}

function FeeRows({ title, total, items, currency }) {
  if (!items?.length && !(total > 0)) {
    return null;
  }

  return (
    <>
      <tr>
        <td className={`${tableCellClassName} font-semibold`} colSpan={2}>
          {title}
          <span className="crm-text-muted ml-2 font-normal">
            {formatMoney(total, currency)}
          </span>
        </td>
      </tr>
      {(items || []).map((item) => (
        <tr key={item.type}>
          <td className={`${tableCellClassName} pl-6 text-[rgb(var(--crm-text-muted))]`}>
            {item.label}
          </td>
          <td className={`${tableCellClassName} text-right tabular-nums`}>
            {formatMoney(item.amount, currency)}
          </td>
        </tr>
      ))}
    </>
  );
}

function RevenueCalculatorPage({ token, user }) {
  const [marketplace, setMarketplace] = useState('IN');
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [itemPrice, setItemPrice] = useState('0');
  const [shippingCharge, setShippingCharge] = useState('0');
  const [fbmFulfilmentCost, setFbmFulfilmentCost] = useState('0');
  const [costOfGoods, setCostOfGoods] = useState('0');
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [mode, setMode] = useState(user?.spApiMode || 'sandbox');
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, ok: 0, error: 0 });
  const bulkFileRef = useRef(null);
  const bulkCancelRef = useRef(false);

  const currency = useMemo(() => (marketplace === 'IN' ? 'INR' : 'USD'), [marketplace]);
  const activeMarketplace = MARKETPLACES.find((entry) => entry.value === marketplace) || MARKETPLACES[0];
  const bulkValidCount = useMemo(
    () => bulkRows.filter((row) => row.asin && !row.invalid).length,
    [bulkRows]
  );

  const fbmEstimate = useMemo(() => {
    if (!comparison?.yourFulfilment) return null;
    return {
      ...comparison.yourFulfilment,
      salesPrice: comparison.salesPrice,
    };
  }, [comparison]);

  const runCalculation = useCallback(
    async (product, overrides = {}) => {
      if (!product?.asin) return;

      setStatus({ type: 'loading', message: 'Calculating fees…' });

      try {
        const payload = await calculateRevenue(token, {
          marketplace,
          asin: product.asin,
          itemPrice: Number(overrides.itemPrice ?? itemPrice) || 0,
          shippingCharge: Number(overrides.shippingCharge ?? shippingCharge) || 0,
          fbmFulfilmentCost: Number(overrides.fbmFulfilmentCost ?? fbmFulfilmentCost) || 0,
          storageCostPerUnit: 0,
          costOfGoods: Number(overrides.costOfGoods ?? costOfGoods) || 0,
        });

        setSelectedProduct(payload.product || product);
        setComparison(payload.comparison || null);
        setMode(payload.mode || mode);
        setStatus({ type: 'ready', message: '' });
      } catch (error) {
        setStatus({ type: 'error', message: error.message });
      }
    },
    [token, marketplace, itemPrice, shippingCharge, fbmFulfilmentCost, costOfGoods, mode]
  );

  const handleSearch = async (event) => {
    event.preventDefault();

    const query = searchInput.trim();
    if (!query) return;

    setStatus({ type: 'loading', message: 'Searching Amazon catalogue…' });

    try {
      const payload = await searchRevenueProducts(token, {
        marketplace,
        search: query,
      });

      setSearchResults(payload.items || []);
      setMode(payload.mode || mode);

      if (payload.items?.length === 1) {
        const product = payload.items[0];
        applySpApiPricing(product, setItemPrice, setShippingCharge);
        setSelectedProduct(product);
        await runCalculation(product, {
          itemPrice: product.featuredOfferPrice ?? 0,
          shippingCharge: product.featuredOfferShipping ?? 0,
        });
      } else if (payload.items?.length > 1) {
        setSelectedProduct(null);
        setComparison(null);
        setStatus({ type: 'ready', message: '' });
      } else {
        setSelectedProduct(null);
        setComparison(null);
        setStatus({
          type: 'ready',
          message: isAsin(query)
            ? 'No product found for this ASIN and marketplace.'
            : 'No products found. Try an ASIN or a more specific title.',
        });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  const handleSelectProduct = async (product) => {
    applySpApiPricing(product, setItemPrice, setShippingCharge);
    setSelectedProduct(product);
    setSearchResults([]);
    await runCalculation(product, {
      itemPrice: product.featuredOfferPrice ?? 0,
      shippingCharge: product.featuredOfferShipping ?? 0,
    });
  };

  useEffect(() => {
    if (!selectedProduct?.asin) return undefined;

    const timer = window.setTimeout(() => {
      runCalculation(selectedProduct);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [itemPrice, shippingCharge, fbmFulfilmentCost, costOfGoods, marketplace, selectedProduct, runCalculation]);

  const resetSearch = () => {
    setSelectedProduct(null);
    setComparison(null);
    setSearchResults([]);
    setSearchInput('');
    setItemPrice('0');
    setShippingCharge('0');
    setFbmFulfilmentCost('0');
    setCostOfGoods('0');
    setStatus({ type: 'idle', message: '' });
  };

  const handleBulkFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBulkResults(null);
    setBulkProgress({ done: 0, total: 0, ok: 0, error: 0 });
    setStatus({ type: 'loading', message: 'Reading Excel…' });

    try {
      const parsed = await parseBulkAsinWorkbook(file);
      const invalid = parsed.filter((row) => row.invalid);
      setBulkRows(parsed);
      setBulkFileName(file.name);
      setStatus({
        type: 'ready',
        message: invalid.length
          ? `Loaded ${parsed.length - invalid.length} ASINs (${invalid.length} invalid rows skipped on run).`
          : `Loaded ${parsed.length} ASIN(s) from ${file.name}.`,
      });
    } catch (error) {
      setBulkRows([]);
      setBulkFileName('');
      setStatus({ type: 'error', message: error.message || 'Failed to read Excel.' });
    } finally {
      if (bulkFileRef.current) {
        bulkFileRef.current.value = '';
      }
    }
  };

  const clearBulk = () => {
    bulkCancelRef.current = true;
    setBulkRows([]);
    setBulkResults(null);
    setBulkFileName('');
    setBulkProgress({ done: 0, total: 0, ok: 0, error: 0 });
    if (bulkFileRef.current) {
      bulkFileRef.current.value = '';
    }
  };

  const runBulkCalculation = async () => {
    const rows = bulkRows.filter((row) => row.asin && !row.invalid);
    if (!rows.length) {
      setStatus({ type: 'error', message: 'Upload an Excel file with valid ASINs first.' });
      return;
    }

    const CHUNK_SIZE = 5;
    const total = rows.length;
    bulkCancelRef.current = false;
    setBulkRunning(true);
    setBulkResults(null);
    setBulkProgress({ done: 0, total, ok: 0, error: 0 });
    setStatus({
      type: 'loading',
      message: `Calculating 0 / ${total}…`,
    });

    const allResults = [];
    let okCount = 0;
    let errorCount = 0;
    let lastMode = mode;

    try {
      for (let start = 0; start < rows.length; start += CHUNK_SIZE) {
        if (bulkCancelRef.current) {
          break;
        }

        const chunk = rows.slice(start, start + CHUNK_SIZE);
        const payload = await calculateRevenueBulk(token, {
          marketplace,
          rows: chunk.map((row) => ({
            asin: row.asin,
            itemPrice: row.itemPrice,
            shippingCharge: row.shippingCharge,
            fbmFulfilmentCost: row.fbmFulfilmentCost ?? 0,
            costOfGoods: row.costOfGoods ?? 0,
          })),
        });

        lastMode = payload.mode || lastMode;
        const chunkResults = (payload.results || []).map((row, index) => ({
          ...row,
          rowNumber: start + (row.rowNumber || index + 1),
        }));

        for (const row of chunkResults) {
          allResults.push(row);
          if (row.ok) okCount += 1;
          else errorCount += 1;
        }

        const done = Math.min(start + chunk.length, total);
        setBulkProgress({ done, total, ok: okCount, error: errorCount });
        setBulkResults({
          marketplace,
          mode: lastMode,
          total: allResults.length,
          okCount,
          errorCount,
          results: [...allResults],
          partial: done < total,
        });
        setStatus({
          type: 'loading',
          message: `Calculating ${done} / ${total}… (${okCount} ok, ${errorCount} failed)`,
        });
      }

    // After all chunks, retry failed ASINs once more (common Amazon throttle misses).
    const failed = allResults.filter((row) => !row.ok && row.asin);
    if (!bulkCancelRef.current && failed.length) {
      setStatus({
        type: 'loading',
        message: `Retrying ${failed.length} failed ASIN(s)…`,
      });

      const retryRows = failed.map((row) => {
        const original = rows.find((item) => item.asin === row.asin) || { asin: row.asin };
        return {
          asin: row.asin,
          itemPrice: original.itemPrice,
          shippingCharge: original.shippingCharge,
          fbmFulfilmentCost: original.fbmFulfilmentCost ?? 0,
          costOfGoods: original.costOfGoods ?? 0,
        };
      });

      for (let start = 0; start < retryRows.length; start += CHUNK_SIZE) {
        if (bulkCancelRef.current) break;
        const chunk = retryRows.slice(start, start + CHUNK_SIZE);
        try {
          const payload = await calculateRevenueBulk(token, {
            marketplace,
            rows: chunk,
          });
          lastMode = payload.mode || lastMode;
          for (const retried of payload.results || []) {
            if (!retried.ok) continue;
            const idx = allResults.findIndex((row) => row.asin === retried.asin && !row.ok);
            if (idx >= 0) {
              if (!allResults[idx].ok) {
                errorCount = Math.max(0, errorCount - 1);
                okCount += 1;
              }
              allResults[idx] = {
                ...retried,
                rowNumber: allResults[idx].rowNumber,
              };
            }
          }
          setBulkProgress({ done: total, total, ok: okCount, error: errorCount });
          setBulkResults({
            marketplace,
            mode: lastMode,
            total: allResults.length,
            okCount,
            errorCount,
            results: [...allResults],
            partial: false,
          });
        } catch {
          // Keep previous failures if retry chunk itself fails.
        }
      }
    }

      setMode(lastMode);
      setBulkResults({
        marketplace,
        mode: lastMode,
        total: allResults.length,
        okCount,
        errorCount,
        results: allResults,
        partial: false,
      });
      setStatus({
        type: 'ready',
        message: bulkCancelRef.current
          ? `Stopped at ${allResults.length} / ${total}: ${okCount} ok, ${errorCount} failed.`
          : `Done: ${okCount} ok, ${errorCount} failed out of ${allResults.length}.`,
      });
    } catch (error) {
      if (allResults.length) {
        setBulkResults({
          marketplace,
          mode: lastMode,
          total: allResults.length,
          okCount,
          errorCount,
          results: allResults,
          partial: true,
        });
      }
      setStatus({
        type: 'error',
        message:
          allResults.length > 0
            ? `${error.message || 'Bulk calculation failed'} (saved ${allResults.length} completed rows).`
            : error.message || 'Bulk calculation failed.',
      });
    } finally {
      setBulkRunning(false);
    }
  };

  const amazonFees = fbmEstimate?.fees?.amazonFees ?? { total: 0, items: [] };
  const otherFeesAndTaxes = fbmEstimate?.fees?.otherFeesAndTaxes ?? { total: 0, items: [] };
  const margin =
    fbmEstimate?.salesPrice > 0
      ? ((fbmEstimate.netProfit || 0) / fbmEstimate.salesPrice) * 100
      : 0;

  return (
    <section className="grid gap-5">
      <StatusBanner
        status={
          status.type === 'error' || (status.type === 'ready' && status.message)
            ? status
            : { type: 'idle', message: '' }
        }
        setStatus={setStatus}
      />

      <div className={panelClassName}>
        <PanelHeader
          title="Bulk ASIN fee check"
          subtitle="Upload an Excel of ASINs, pull Amazon FBM commissions, and download results as Excel (max 200)."
        />
        <div className="space-y-4 px-5 pb-5">
          <div className="grid gap-3 lg:grid-cols-[11rem_minmax(0,1fr)_auto_auto] lg:items-end">
            <label className="grid gap-1.5">
              <span className={labelClassName}>Marketplace</span>
              <select
                value={marketplace}
                onChange={(event) => setMarketplace(event.target.value)}
                className={inputClassName}
                disabled={bulkRunning}
              >
                {MARKETPLACES.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label} ({entry.value})
                  </option>
                ))}
              </select>
            </label>

            <label className="grid gap-1.5">
              <span className={labelClassName}>Excel file</span>
              <input
                ref={bulkFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleBulkFileChange}
                disabled={bulkRunning}
                className={`${inputClassName} file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:font-semibold`}
              />
            </label>

            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={downloadBulkTemplate}
              disabled={bulkRunning}
            >
              Download template
            </button>

            <button
              type="button"
              className={primaryButtonClassName}
              onClick={runBulkCalculation}
              disabled={bulkRunning || bulkValidCount === 0}
            >
              {bulkRunning
                ? `${bulkProgress.done} / ${bulkProgress.total}`
                : `Run bulk (${bulkValidCount})`}
            </button>
          </div>

          {bulkRunning || bulkProgress.total > 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-semibold text-slate-800">
                  {bulkRunning ? 'Progress' : 'Last run'}
                  {': '}
                  <span className="tabular-nums">
                    {bulkProgress.done} / {bulkProgress.total || bulkValidCount}
                  </span>
                </span>
                <span className="crm-text-muted text-xs tabular-nums">
                  {bulkProgress.ok} ok · {bulkProgress.error} failed
                  {bulkProgress.total > 0
                    ? ` · ${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%`
                    : ''}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    bulkRunning ? 'bg-blue-600' : 'bg-emerald-500'
                  }`}
                  style={{
                    width: `${
                      bulkProgress.total
                        ? Math.min(100, Math.round((bulkProgress.done / bulkProgress.total) * 100))
                        : 0
                    }%`,
                  }}
                />
              </div>
              {bulkRunning ? (
                <p className="crm-text-muted mt-2 text-xs">
                  Calculating Amazon fees… keep this tab open. Results update as each batch finishes.
                </p>
              ) : null}
            </div>
          ) : null}

          <p className="crm-text-muted text-xs leading-5">
            Columns: <strong>(Parent) ASIN</strong> (required) + your{' '}
            <strong>Cost of Goods</strong> price (column can be named{' '}
            <code>cost of the product @05%</code> — we use the number you type, no 5% calculation).
            SKU is ignored. Fees and proceeds use your COGS as entered.
            {bulkFileName ? (
              <>
                {' '}
                Loaded file: <span className="font-medium text-slate-700">{bulkFileName}</span>
                {bulkRows.length ? ` · ${bulkValidCount} valid ASIN(s)` : ''}.
              </>
            ) : null}
          </p>

          {bulkRows.length || bulkResults ? (
            <div className="flex flex-wrap gap-2">
              {bulkResults?.results?.length ? (
                <button
                  type="button"
                  className={primaryButtonClassName}
                  onClick={() =>
                    downloadBulkResultsWorkbook(bulkResults.results, marketplace)
                  }
                >
                  Download results Excel
                </button>
              ) : null}
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={clearBulk}
                disabled={bulkRunning}
              >
                Clear bulk
              </button>
            </div>
          ) : null}

          {bulkResults?.results?.length ? (
            <div className="overflow-x-auto rounded-md border border-[rgb(var(--crm-border))]">
              <table className="w-full min-w-[56rem] border-collapse">
                <thead>
                  <tr>
                    <th className={tableHeadClassName}>ASIN</th>
                    <th className={tableHeadClassName}>Title</th>
                    <th className={`${tableHeadClassName} text-right`}>Sales</th>
                    <th className={`${tableHeadClassName} text-right`}>Amazon fees</th>
                    <th className={`${tableHeadClassName} text-right`}>Proceeds</th>
                    <th className={tableHeadClassName}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.results.slice(0, 25).map((row) => (
                    <tr key={`${row.rowNumber}-${row.asin}`}>
                      <td className={tableCellClassName}>
                        <CopyableValue value={row.asin || '—'} />
                      </td>
                      <td className={`${tableCellClassName} max-w-[18rem]`}>
                        <span className="line-clamp-2 text-sm">
                          {row.ok ? row.title || '—' : row.error || 'Failed'}
                        </span>
                      </td>
                      <td className={`${tableCellClassName} text-right tabular-nums`}>
                        {row.ok
                          ? formatMoney(row.salesPrice, row.currency || currency)
                          : '—'}
                      </td>
                      <td className={`${tableCellClassName} text-right tabular-nums`}>
                        {row.ok
                          ? formatMoney(row.amazonFeesTotal, row.currency || currency)
                          : '—'}
                      </td>
                      <td className={`${tableCellClassName} text-right tabular-nums font-medium`}>
                        {row.ok
                          ? formatMoney(row.estimatedProceeds, row.currency || currency)
                          : '—'}
                      </td>
                      <td className={tableCellClassName}>
                        {row.ok ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            OK
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                            Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bulkResults.results.length > 25 ? (
                <p className="crm-text-muted border-t border-[rgb(var(--crm-border))] px-4 py-2 text-xs">
                  Showing first 25 rows — download Excel for the full file.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {!selectedProduct ? (
        <div className={panelClassName}>
          <PanelHeader
            title="Search product"
            subtitle="Look up an ASIN or title, then estimate FBM fees from Amazon Product Fees."
          />
          <div className="space-y-4 px-5 pb-5">
            <form
              onSubmit={handleSearch}
              className="grid gap-3 lg:grid-cols-[11rem_minmax(0,1fr)_auto] lg:items-end"
            >
              <label className="grid gap-1.5">
                <span className={labelClassName}>Marketplace</span>
                <select
                  value={marketplace}
                  onChange={(event) => setMarketplace(event.target.value)}
                  className={inputClassName}
                >
                  {MARKETPLACES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label} ({entry.value})
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-1.5">
                <span className={labelClassName}>Search</span>
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="ASIN, title, SKU, ISBN…"
                  className={inputClassName}
                />
              </label>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className={primaryButtonClassName}
              >
                {status.type === 'loading' ? 'Searching…' : 'Search'}
              </button>
            </form>

            {searchResults.length > 0 ? (
              <div className="overflow-x-auto rounded-md border border-[rgb(var(--crm-border))]">
                <table className="w-full min-w-[40rem] border-collapse">
                  <thead>
                    <tr>
                      <th className={tableHeadClassName}>Product</th>
                      <th className={tableHeadClassName}>ASIN</th>
                      <th className={tableHeadClassName}>Brand</th>
                      <th className={tableHeadClassName}>Price</th>
                      <th className={tableHeadClassName} />
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((product) => (
                      <tr key={product.asin}>
                        <td className={tableCellClassName}>
                          <div className="flex items-center gap-3">
                            <ProductThumb
                              item={{
                                title: product.title,
                                asinIn: product.marketplace === 'IN' ? product.asin : null,
                                asinCom: product.marketplace === 'US' ? product.asin : null,
                                imageUrl: product.imageUrl,
                              }}
                              marketplace={product.marketplace}
                              className="h-10 w-10"
                            />
                            <span className="line-clamp-2 text-sm font-medium">{product.title}</span>
                          </div>
                        </td>
                        <td className={tableCellClassName}>
                          <CopyableValue value={product.asin} />
                        </td>
                        <td className={tableCellClassName}>{product.brand || '—'}</td>
                        <td className={tableCellClassName}>
                          {product.featuredOfferPrice != null
                            ? formatMoney(
                                product.featuredOfferPrice,
                                product.featuredOfferCurrency || currency
                              )
                            : '—'}
                        </td>
                        <td className={tableCellClassName}>
                          <button
                            type="button"
                            className={secondaryButtonClassName}
                            onClick={() => handleSelectProduct(product)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {status.message && status.type === 'ready' && searchResults.length === 0 ? (
              <p className="crm-text-muted text-sm">{status.message}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <div className={panelClassName}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[rgb(var(--crm-border))] px-5 py-4">
              <div>
                <p className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
                  Selected product
                </p>
                <h3 className="mt-1 max-w-3xl text-base font-semibold leading-snug">
                  {selectedProduct.title}
                </h3>
              </div>
              <button type="button" className={secondaryButtonClassName} onClick={resetSearch}>
                Search another
              </button>
            </div>

            <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-start">
              <ProductThumb
                item={{
                  title: selectedProduct.title,
                  asinIn: selectedProduct.marketplace === 'IN' ? selectedProduct.asin : null,
                  asinCom: selectedProduct.marketplace === 'US' ? selectedProduct.asin : null,
                  imageUrl: selectedProduct.imageUrl,
                }}
                marketplace={selectedProduct.marketplace}
                className="h-20 w-20"
              />

              <dl className="grid min-w-0 flex-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="crm-text-muted">ASIN</dt>
                  <dd className="mt-0.5 font-medium">
                    <CopyableValue value={selectedProduct.asin} />
                  </dd>
                </div>
                <div>
                  <dt className="crm-text-muted">Marketplace</dt>
                  <dd className="mt-0.5 font-medium">
                    {activeMarketplace.label} ({marketplace}) · FBM
                  </dd>
                </div>
                <div>
                  <dt className="crm-text-muted">Brand</dt>
                  <dd className="mt-0.5 font-medium">{selectedProduct.brand || '—'}</dd>
                </div>
                <div>
                  <dt className="crm-text-muted">Weight / dimensions</dt>
                  <dd className="mt-0.5 font-medium">
                    {selectedProduct.weightKg ? `${selectedProduct.weightKg} kg` : '—'}
                    {' · '}
                    {formatDimensions(selectedProduct.dimensionsCm)}
                  </dd>
                </div>
                {selectedProduct.featuredOfferPrice != null ? (
                  <div className="sm:col-span-2">
                    <dt className="crm-text-muted">Lowest offer</dt>
                    <dd className="mt-0.5 font-medium tabular-nums">
                      {formatMoney(
                        selectedProduct.featuredOfferPrice,
                        selectedProduct.featuredOfferCurrency || currency
                      )}
                      {' + '}
                      {formatMoney(
                        selectedProduct.featuredOfferShipping ?? 0,
                        selectedProduct.featuredOfferCurrency || currency
                      )}{' '}
                      shipping
                      {selectedProduct.offerCount != null
                        ? ` · ${selectedProduct.offerCount} offers`
                        : ''}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>

          {fbmEstimate ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <StatCard
                label="Sales price"
                value={formatMoney(fbmEstimate.salesPrice, currency)}
                detail="Item + shipping to buyer"
              />
              <StatCard
                label="Total costs"
                value={formatMoney(fbmEstimate.totalCosts, currency)}
                detail="Amazon fees + your costs"
              />
              <StatCard
                label="Estimated proceeds"
                value={formatMoney(fbmEstimate.netProfit, currency)}
                detail={`${margin >= 0 ? '+' : ''}${margin.toFixed(1)}% of sales price`}
                status={(fbmEstimate.netProfit ?? 0) >= 0 ? 'success' : 'warning'}
              />
            </div>
          ) : null}

          {fbmEstimate ? (
            <div className="grid gap-5 lg:grid-cols-2">
              <div className={panelClassName}>
                <PanelHeader
                  title="Inputs"
                  subtitle="Adjust price and your costs. Fees recalculate automatically."
                />
                <div className="grid gap-4 px-5 pb-5 sm:grid-cols-2">
                  <CurrencyInput
                    label="Item price"
                    value={itemPrice}
                    onChange={setItemPrice}
                    currency={currency}
                  />
                  <CurrencyInput
                    label="Shipping charge"
                    value={shippingCharge}
                    onChange={setShippingCharge}
                    currency={currency}
                  />
                  <CurrencyInput
                    label="Fulfilment cost"
                    value={fbmFulfilmentCost}
                    onChange={setFbmFulfilmentCost}
                    currency={currency}
                    hint="Pick, pack, ship, packaging"
                  />
                  <CurrencyInput
                    label="Cost of goods"
                    value={costOfGoods}
                    onChange={setCostOfGoods}
                    currency={currency}
                    hint="Landed product cost"
                  />
                </div>
              </div>

              <div className={`${panelClassName} overflow-x-auto`}>
                <PanelHeader title="Fee breakdown" subtitle="From Amazon Product Fees (FBM)." />
                <table className="w-full border-collapse px-0">
                  <thead>
                    <tr>
                      <th className={tableHeadClassName}>Line</th>
                      <th className={`${tableHeadClassName} text-right`}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={tableCellClassName}>Sales price</td>
                      <td className={`${tableCellClassName} text-right tabular-nums font-medium`}>
                        {formatMoney(fbmEstimate.salesPrice, currency)}
                      </td>
                    </tr>
                    <FeeRows
                      title="Amazon fees"
                      total={amazonFees.total}
                      items={amazonFees.items}
                      currency={currency}
                    />
                    <FeeRows
                      title="Other fees & taxes"
                      total={otherFeesAndTaxes.total}
                      items={otherFeesAndTaxes.items}
                      currency={currency}
                    />
                    <tr>
                      <td className={tableCellClassName}>Your fulfilment cost</td>
                      <td className={`${tableCellClassName} text-right tabular-nums`}>
                        {formatMoney(Number(fbmFulfilmentCost) || 0, currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className={tableCellClassName}>Cost of goods</td>
                      <td className={`${tableCellClassName} text-right tabular-nums`}>
                        {formatMoney(Number(costOfGoods) || 0, currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className={`${tableCellClassName} font-semibold`}>Total costs</td>
                      <td className={`${tableCellClassName} text-right tabular-nums font-semibold`}>
                        {formatMoney(fbmEstimate.totalCosts, currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className={`${tableCellClassName} font-semibold`}>Estimated proceeds</td>
                      <td className={`${tableCellClassName} text-right tabular-nums font-semibold`}>
                        {formatMoney(fbmEstimate.netProfit, currency)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : status.type === 'loading' ? (
            <p className="crm-text-muted text-sm">Calculating fees…</p>
          ) : (
            <div className={panelClassName}>
              <PageHeader
                title="Enter price details"
                subtitle="Set item price and costs to calculate Amazon FBM fees."
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default RevenueCalculatorPage;
